import React from 'react';
import ReviewOutput from '@app/model/review/output/ReviewOutput';
import Modal, { ModalProps } from '@app/component/overlays/modal/Modal';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import useAppForm from '@app/site/_form';
import * as z from 'zod/v4';
import { SubmitButton } from '@app/site/_form/button/SubmitButton';
import { ReviewService } from '@app/model/review/service/ReviewService';
import { useReviewsTools } from '@app/site/secured/review-manager/provider/useReviewsTools';
import { useQueryClient } from '@tanstack/react-query';
import ReviewHandlingSessionLogEntry from '@app/model/review/ReviewHandlingSessionLogEntry';

type ReviewMessageInternalCommentModalProps = {
	reviewOutput: ReviewOutput,
	logEntry?: ReviewHandlingSessionLogEntry
} & ModalProps

const ReviewMessageInternalCommentModal = React.memo(
	function ReviewMessageInternalCommentModal({ reviewOutput, logEntry,...props }: ReviewMessageInternalCommentModalProps) {
		const toaster = useToaster();
		const tools = useReviewsTools();
		const queryClient = useQueryClient();

		const ReviewInternalCommentInputValidation = z.object({
			reviewId: z.number(),
			logEntryId: z.number().nullable(),
			notes: z.string().nonempty('Comment is required'),
		});

		const form = useAppForm({
			defaultValues: {
				reviewId: reviewOutput.review.id,
				logEntryId: !!logEntry ? logEntry.id : null,
				notes: !!logEntry ? logEntry.notes : '',
			},
			validators: {
				onChange: ReviewInternalCommentInputValidation,
			},
			onSubmit: ({ value }) =>
				ReviewService.saveReviewInternalNotes(value, tools.filter, queryClient)
					.then(()=>{
						props.ref.current!.close();
						toaster.success('Review Internal Note', 'Internal note added successfully');
					})
					.catch((e) => {
					toaster.error('Review Internal Note', e.message);
				}),
		});

		const onClose = () => {
			form.reset();
			if(!!props.onClose) props.onClose();
		}

		return <form.AppForm>
			<form onSubmit={
				(e) => {
					e.preventDefault();
					e.stopPropagation();
				}
			}>
				<Modal title="Internal Note" {...props} onClose={onClose} actions={
					<SubmitButton state="primary">Save</SubmitButton>
				}>
					<form.AppField name="notes">
						{(field) => <field.TextAreaField extraClassName="w-full" placeholder="Insert note" />}
					</form.AppField>
				</Modal>
			</form>
		</form.AppForm>;
	},
);

export default ReviewMessageInternalCommentModal;